import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CalendarDays } from "lucide-react";
import SectionHeading from "@/components/common/SectionHeading";
import EventCard from "@/components/common/EventCard";
import AdBanner from "@/components/common/AdBanner";
import { DocumentList } from "@/components/common/Media";
import { Spinner, EmptyState, Badge } from "@/components/common/Primitives";
import { formatDate } from "@/lib/format";
import {
  isInitialLoading,
  useForum,
  useForumPosts,
  useForumDocuments,
  useForumEvents,
} from "@/hooks/useSiteQueries";

const POST_TONES = { campaign: "maroon", achievement: "gold", update: "gray" };

export default function ForumPage() {
  const { slug } = useParams();
  const forumQuery = useForum(slug);
  const postsQuery = useForumPosts(slug);
  const docsQuery = useForumDocuments(slug);
  const eventsQuery = useForumEvents(slug);

  const queries = [forumQuery, postsQuery, docsQuery, eventsQuery];
  if (isInitialLoading(queries)) return <Spinner className="py-32" />;

  const forum = forumQuery.data;
  const posts = postsQuery.data;
  const documents = docsQuery.data;
  const events = eventsQuery.data;

  if (!forum) {
    return (
      <div className="container-bcs py-24">
        <EmptyState title="Forum not found" message="The forum you are looking for does not exist." />
      </div>
    );
  }

  return (
    <div data-testid={`forum-page-${slug}`}>
      {/* Hero */}
      <section className="relative bg-maroon text-white overflow-hidden">
        <img src={forum.hero_image} alt={forum.name} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-maroon via-maroon/90 to-maroon/60" />
        <div className="relative container-bcs py-20 sm:py-28">
          <Link to="/our-work" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors mb-6">
            <ArrowLeft size={15} /> Forums
          </Link>
          <h1 className="heading-xl !text-white max-w-3xl">{forum.name}</h1>
          <p className="mt-3 font-serif italic text-xl text-gold-light">{forum.tagline}</p>
          <p className="mt-6 text-white/85 max-w-2xl leading-relaxed">{forum.description}</p>
        </div>
      </section>

      <section className="container-bcs py-8 sm:py-10">
        <AdBanner placement="forum_page" />
      </section>

      {/* Focus areas */}
      <section className="container-bcs py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(forum.focus_areas || []).map((f, i) => (
            <div key={i} className="card-bcs p-5">
              <p className="font-serif text-3xl text-gold-dark font-semibold">{String(i + 1).padStart(2, "0")}</p>
              <p className="mt-2 text-sm text-ink-soft leading-relaxed">{f}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Posts */}
      <section className="bg-cream-alt border-y border-cream-dark">
        <div className="container-bcs py-16 sm:py-20">
          <SectionHeading eyebrow="Updates & Campaigns" title="From the forum" />
          {posts.length === 0 ? (
            <EmptyState title="No posts yet" />
          ) : (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.map((post) => (
                <article key={post.id} className="card-bcs overflow-hidden flex flex-col" data-testid={`forum-post-${post.id}`}>
                  {post.image_url && (
                    <img src={post.image_url} alt={post.title} className="h-52 w-full object-cover" loading="lazy" />
                  )}
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-center gap-3">
                      <Badge tone={POST_TONES[post.post_type] || "gray"}>{post.post_type}</Badge>
                      <span className="text-xs text-ink-muted flex items-center gap-1.5">
                        <CalendarDays size={13} /> {formatDate(post.created_at)}
                      </span>
                    </div>
                    <h3 className="mt-4 font-serif text-xl font-semibold text-ink leading-snug">{post.title}</h3>
                    <p className="mt-3 text-sm text-ink-soft leading-relaxed flex-1">{post.body}</p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Related events + documents */}
      <section className="container-bcs py-16 sm:py-20 space-y-16">
        {events.length > 0 && (
          <div>
            <SectionHeading eyebrow="Events" title="Forum events" />
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {events.slice(0, 3).map((e) => (
                <EventCard key={e.slug} event={e} />
              ))}
            </div>
          </div>
        )}
        <DocumentList documents={documents} title="Forum downloads" />
      </section>
    </div>
  );
}
